import { Box, CircularProgress, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";

import { PieChartData } from "../../types/leads.types";

/**
 * Generic Pie Chart Component
 * 
 * @param data - Object with string keys and number values (e.g., { crossSell: 10, upsell: 20, newBusiness: 15 })
 * @param isLoading - Show loading spinner when true
 * @param title - Chart title
 * @param colors - Array of colors for pie slices (optional)
 * @param showOnlyNonZero - Filter out zero values (default: true)
 * 
 * Example usage:
 * <LeadsPieChart 
 *   data={{ crossSell: 10, upsell: 20, newBusiness: 15 }} 
 *   title="Lead Distribution" 
 *   colors={["#0088FE", "#00C49F", "#FFBB28"]}
 * />
 */
interface Props {
  data?: PieChartData | null;
  isLoading?: boolean;
  title?: string;
  colors?: string[];
  showOnlyNonZero?: boolean;
  hasError?:boolean;
}

const LeadsPieChart = ({ 
  data, 
  isLoading = false, 
  title = "Distribution Chart",
  colors = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D", "#FFC658", "#FF7C7C"],
  showOnlyNonZero = true,
  hasError=false
}: Props) => {
  const seriesData = data ? data.map((item, index) => ({
      value: item?.value,
      // Convert camelCase to Title Case for better display
      label: item?.label?.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()),
      color: colors[index % colors.length]
    }))
    .filter(item => showOnlyNonZero ? item.value > 0 : true)
    .sort((a, b) => b.value - a.value) : []

  return (
    <Box sx={{ 
      width: "100%", 
      height: 450, 
      border: "solid 1px #0000001F", 
      padding: "16px", 
      display: "flex", 
      flexDirection: "column", 
      borderRadius: "24px", 
      marginBottom: "50px" 
    }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <Typography variant="title_medium">{title}</Typography>
      </Box>
      {
        isLoading ? (
          <Box sx={{ 
            width: "100%", 
            height: 300, 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center" 
          }}>
            <CircularProgress />
          </Box>
        ) 
        : hasError
        ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography color="text.secondary">
             Error in displaying graph data
            </Typography>
          </Box>)
        :(
          <PieChart
            margin={{ bottom: 20, left: 100, right: 100 }}
            series={[
              {
                data: seriesData,
              },
            ]}
            height={250}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: -30,
              },
            }}
          />
        )
      }
    </Box>
  )
}

export default LeadsPieChart;